import { useEffect, useState } from 'react'
import { MapPin } from 'lucide-react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { AsyncMultiSelect } from '@/shared/ui/filters'
import { explain } from '@/shared/api/problem'
import { UnitCascade, type CascadeValue } from '@/features/voters/UnitCascade'
import { listMobilizers } from '@/features/mobilizers/api'
import { roleLabel } from '@/features/mobilizers/vocab'
import type { SurveyCrewReportDto, SurveyResponseDto } from '@/shared/api/types'
import { postCrew } from './api'
import { DateTimePicker } from '@/components/erp/date-picker'

/** `datetime-local` speaks local wall-clock with no zone; `dueAt` is an Instant. */
function fromLocalInput(value: string): string | null {
  if (!value.trim()) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/**
 * Posting a crew to one place.
 *
 * ## Why one unit for the whole crew, and not a station per row
 *
 * Because that is how a supervisor works: a station at a time, naming the
 * people who will be on it. A forty-row table with a unit cascade in every row
 * is a form nobody completes, and the server takes explicit pairs precisely so
 * that this dialog can be run once per station without the pairing being
 * guessed for anybody.
 *
 * ## Why the fence is not set here
 *
 * It is not a property of a posting. The station already has coordinates and
 * the radius belongs to the poll, so posting people to a station IS fencing
 * them — `GET /surveys/{id}/bundle` resolves each interviewer's fence from
 * their own posting. If the poll's fence is OFF the report says so in a
 * sentence; this dialog will not turn it on, because a poll that started
 * refusing interviews because somebody posted a roster would be a side effect
 * nobody asked for.
 *
 * ## It reports, and the report is the point
 *
 * `postCrew` books what it can and names what it cannot, per person. The report
 * stays on screen after the save — a toast saying "37 posted" leaves somebody
 * guessing which three were not.
 */
export function CrewDialog({ open, poll, onClose, onSaved }: {
  open: boolean
  poll: SurveyResponseDto
  onClose: () => void
  onSaved: () => void
}) {
  const [picked, setPicked] = useState<{ value: string; label: string }[]>([])
  const [unit, setUnit] = useState<CascadeValue>({ path: [] })
  const [targetEach, setTargetEach] = useState('')
  const [dueAt, setDueAt] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [report, setReport] = useState<SurveyCrewReportDto | null>(null)

  useEffect(() => {
    if (!open) return
    setPicked([])
    setUnit({ path: [] })
    setTargetEach('')
    setDueAt('')
    setError(null)
    setReport(null)
  }, [open, poll.id])

  const unitId = unit.unitId ?? poll.scopeUnitId
  const fenced = poll.fenceModeCode && poll.fenceModeCode !== 'OFF'
  const fieldworkOver = poll.statusCode === 'CLOSED' || poll.statusCode === 'ARCHIVED'

  async function submit() {
    if (!unitId || picked.length === 0) return
    setBusy(true)
    setError(null)
    try {
      setReport(await postCrew(poll.id, {
        postings: picked.map((p) => ({ mobilizerId: p.value, unitId })),
        // '' is "no target" and 0 is "a target of nobody". Number('') is 0,
        // which turns the first silently into the second.
        targetCountEach: targetEach.trim() === '' ? null : Number(targetEach),
        dueAt: fromLocalInput(dueAt),
      }))
      onSaved()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="md"
      title={`Post a crew to ${poll.ref}`}
      description="Several interviewers, one place, one target each. Run it once per station."
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            {report ? 'Done' : 'Cancel'}
          </button>
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={busy || picked.length === 0 || !unitId} onClick={submit}>
            {busy ? 'Posting…'
              : picked.length === 0 ? 'Post the crew'
              : `Post ${picked.length} ${picked.length === 1 ? 'person' : 'people'}`}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        {fieldworkOver && (
          <Note tone="bad">
            {poll.ref} is {poll.statusCode}, so fieldwork is over and the server will refuse
            every posting. The control is still here because the refusal names the poll and the
            status, and a greyed-out button cannot.
          </Note>
        )}

        <Field label="The crew" required
          hint="Active field staff only. Everyone picked is posted to the same place.">
          <AsyncMultiSelect
            label="Interviewers"
            selected={picked}
            onChange={setPicked}
            placeholder="Search by name"
            emptyLabel="Nobody chosen"
            search={async (term) => {
              const page = await listMobilizers({ q: term, status: 'ACTIVE' }, undefined, 25)
              return page.content.map((m) => ({
                value: m.id,
                label: m.fullName,
                sub: `${roleLabel(m.roleCode)} · ${m.primaryUnitName ?? 'no unit'}`,
              }))
            }}
          />
        </Field>

        <Field label="Where"
          hint="A polling station is the usual answer. Leave it alone to post the whole crew to the poll's scope.">
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2 text-erp-sm">
              <span className="text-muted-foreground">Defaults to the poll's scope:</span>
              <span>{poll.scopeUnitName ?? poll.scopeUnitId ?? 'no scope on this poll'}</span>
            </div>
            <UnitCascade value={unit} onChange={setUnit} />
          </div>
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Target each"
            hint="Applied to everybody. Per-person targets are edited on the assignment afterwards.">
            <input className="erp-input h-9 w-full" type="number" min={0}
              value={targetEach} onChange={(e) => setTargetEach(e.target.value)}
              placeholder="No target" />
          </Field>
          <Field label="Due">
            <DateTimePicker value={dueAt} onChange={setDueAt} className="h-9 w-full" />
          </Field>
        </div>

        {fenced ? (
          <Note tone="info">
            <MapPin className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
            <span className="font-medium">
              Posting somebody to a station is what fences them.
            </span>
            {' This poll’s fence is '}
            {poll.fenceModeCode === 'HARD' ? 'HARD, so an interview taken outside it is refused'
              : 'SOFT, so an interview taken outside it is kept and flagged'}
            {`, and the radius is ${poll.fenceRadiusM} m. Each interviewer is checked against `}
            {'the unit on their OWN posting, not against the whole poll — so a county-wide poll '}
            {'worked station by station is as many fences as there are stations.'}
          </Note>
        ) : (
          <Note tone="warn">
            <span className="font-medium">This poll has no fence.</span>
            {' The postings will be recorded and the stations will show on every assignment, '}
            {'but no interview will be checked against them. Turn the fence on in the poll’s '}
            {'configuration — step 4, “Where it may be answered”.'}
          </Note>
        )}

        {report && (
          <div className="space-y-2 border-t border-border pt-3">
            <p className="text-erp-sm">
              <span className="font-medium">{report.created.length} posted.</span>
              {report.refused.length > 0
                && ` ${report.refused.length} could not be — each one says why.`}
            </p>

            {report.fenceNote && <Note tone="warn">{report.fenceNote}</Note>}

            {report.created.length > 0 && (
              <ul className="space-y-0.5">
                {report.created.map((a) => (
                  <li key={a.id} className="text-erp-xs">
                    <span className="font-medium">{a.mobilizerName ?? 'Someone'}</span>
                    {` · ${a.scopeUnitName ?? 'no unit'}`}
                    {a.targetCount > 0 && ` · ${a.targetCount} interviews`}
                  </li>
                ))}
              </ul>
            )}

            {report.refused.length > 0 && (
              <ul className="space-y-1">
                {report.refused.map((r, i) => (
                  <li key={`${r.mobilizerId}-${i}`}
                    className="text-erp-xs text-destructive">
                    <span className="font-medium">{r.mobilizerName ?? r.mobilizerId}</span>
                    {' — '}{r.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}
